import { memo, useCallback, useRef, useState } from 'react'

import { ReportOffOutlined } from '@mui/icons-material'
import { Box, Typography } from '@mui/material'
import { DistributedTaskLogEntry } from '@nativewaves/nw-cloud-sdk/smep'
import { useInfiniteQuery } from '@tanstack/react-query'
import { Table as ReactTableType } from '@tanstack/react-table'
import { pick } from 'lodash-es'
import { useTranslation } from 'react-i18next'

import { Editor } from '@shared/components/Editor'
import { ScrollToTopButton, Table } from '@shared/components/Table'
import { BigSideDrawer, LoadingIndicator } from '@shared/layouts'
import { SCROLL_BUTTON_ID } from 'domains/Workspaces/Events/Event/Monitoring/consts'
import { useColumns } from 'domains/Workspaces/Events/Event/Monitoring/Logs'
import { eventMonitoringRoute } from 'pages/NWPlatform/Workspaces/Workspace/Events/Event'
import { eventQueryKeys } from 'services/queryKeys'
import { RefetchIntervals } from 'types/query'

type OverviewProps = {
  toggleAutoRefresh: (state: boolean) => void
}

const OverviewRaw = ({ toggleAutoRefresh }: OverviewProps) => {
  const { t } = useTranslation(['common', 'domain'])

  const [refetchInterval, setRefetchInterval] = useState<RefetchIntervals | 0>(RefetchIntervals.FAST)
  const [viewItem, setViewItem] = useState<DistributedTaskLogEntry | null>(null)

  const tableInstance = useRef<ReactTableType<DistributedTaskLogEntry>>(undefined)

  const { eventId } = eventMonitoringRoute.useParams()
  const queryStrings = eventMonitoringRoute.useSearch()

  const eventLogsQuery = useInfiniteQuery({
    ...eventQueryKeys.logs({ eventId, limit: 50, ...queryStrings }),
    refetchInterval,
    select: (data) => ({ flat: data?.pages.flatMap((page) => page.items) }),
  })
  const logs = eventLogsQuery.data?.flat ?? []

  const columns = useColumns()

  const handleScroll = useCallback(
    (scrollTop: number) => {
      if (scrollTop > 0) {
        setRefetchInterval(0)
        toggleAutoRefresh(false)
      } else {
        setRefetchInterval(RefetchIntervals.FAST)
        toggleAutoRefresh(true)
      }
    },
    [toggleAutoRefresh],
  )

  if (eventLogsQuery.isLoading) {
    return (
      <Box sx={{ height: '100%' }}>
        <LoadingIndicator />
      </Box>
    )
  }

  if (!logs.length) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          height: '100%',
          justifyContent: 'center',
          gap: 1,
        }}
      >
        <ReportOffOutlined />
        <Typography variant="heading-md">{t('domain:Workspace.Event.noLogs')}</Typography>
      </Box>
    )
  }

  return (
    <>
      <Table
        getTableInstance={(instance) => (tableInstance.current = instance)}
        data={logs}
        columns={columns}
        isInitialLoadingGlobal={eventLogsQuery.isLoading}
        enableClickAction
        clickAction={(row) => setViewItem(row.original)}
        hideHeader
        autoSizeRows
        onScroll={handleScroll}
        enableInfinity
        infinityProps={pick(eventLogsQuery, ['hasNextPage', 'isFetchingNextPage', 'fetchNextPage'])}
        ScrollTopButton={(parentRef) => (
          <ScrollToTopButton
            id={SCROLL_BUTTON_ID}
            onClick={() => parentRef.current?.scrollTo({ top: 0, behavior: 'smooth' })}
            tooltipTitle={t('domain:Workspace.Event.enableAutoRefresh')}
          />
        )}
      />

      <BigSideDrawer
        DrawerProps={{ open: !!viewItem, onClose: () => setViewItem(null) }}
        ContentBoxSx={{
          display: 'flex',
          flexDirection: 'column',
          p: 1,
          pl: 9,
          pt: 4,
          bgcolor: 'background.levels.700',
        }}
        title={
          <Typography variant="heading-md">
            {t(`domain:Workspace.Event.${viewItem?.type === 'Log' ? 'log' : 'alert'}Details`)}
          </Typography>
        }
        progress={100}
      >
        <Editor value={viewItem} readOnly />
      </BigSideDrawer>
    </>
  )
}

export const Overview = memo(OverviewRaw)
